import { Flex, Form, Input, Modal, Space } from 'antd'
import {
  type ChangeEvent,
  FC,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'

import {
  getJustificationVariantProps,
  type JustificationButtonVariant,
} from './justificationButtonVariant'
import { validateJustification } from './justificationValidation'

const TEXTAREA_ROWS = 4

export interface JustificationDialogProps {
  open: boolean
  title: string
  message?: string
  inputLabel: string
  placeholder?: string
  minLength: number
  maxLength: number
  submitLabel: string
  variant?: JustificationButtonVariant
  text: string
  onTextChange: (text: string) => void
  /**
   * Receives the trimmed text. Owns its own error reporting; the dialog closes
   * either way.
   */
  onSubmit: (justification: string) => void | Promise<void>
  onClose: () => void
  /** Rendered above the justification, e.g. the value being justified. */
  children?: ReactNode
  /** Blocks submit for reasons outside the justification text. */
  submitDisabled?: boolean
  /** A secondary action at the left of the footer, e.g. a reset. */
  footerStart?: ReactNode
}

export const JustificationDialog: FC<JustificationDialogProps> = ({
  open,
  title,
  message,
  inputLabel,
  placeholder,
  minLength,
  maxLength,
  submitLabel,
  variant = 'neutral',
  text,
  onTextChange,
  onSubmit,
  onClose,
  children,
  submitDisabled = false,
  footerStart,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validation = useMemo(
    () => validateJustification(text, { minLength, maxLength }),
    [text, minLength, maxLength]
  )

  const runSubmit = useCallback(async () => {
    setIsSubmitting(true)
    try {
      await onSubmit(text.trim())
    } finally {
      setIsSubmitting(false)
      onClose()
    }
  }, [onSubmit, onClose, text])

  const handleSubmit = useCallback(() => {
    runSubmit().catch(() => {
      // `onSubmit` owns error reporting; nothing to surface from here.
    })
  }, [runSubmit])

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) =>
      onTextChange(event.target.value),
    [onTextChange]
  )

  const okButtonProps = useMemo(
    () => ({
      ...getJustificationVariantProps(variant),
      disabled: !validation.valid || submitDisabled,
    }),
    [variant, validation.valid, submitDisabled]
  )
  const mask = useMemo(() => ({ closable: !isSubmitting }), [isSubmitting])

  // An untouched textarea reads as a failure if it opens already showing red.
  const showError = text.length > 0 && !validation.valid

  return (
    <Modal
      title={title}
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText={submitLabel}
      okButtonProps={okButtonProps}
      confirmLoading={isSubmitting}
      closable={!isSubmitting}
      mask={mask}
      footer={
        footerStart
          ? (_, { OkBtn, CancelBtn }) => (
              <Flex justify='space-between'>
                {footerStart}
                <Space>
                  <CancelBtn />
                  <OkBtn />
                </Space>
              </Flex>
            )
          : undefined
      }
      destroyOnHidden>
      {message && <Message>{message}</Message>}
      <Form layout='vertical'>
        {children}
        <Form.Item
          label={inputLabel}
          required
          validateStatus={showError ? 'error' : ''}
          help={showError ? validation.error : undefined}>
          <Input.TextArea
            rows={TEXTAREA_ROWS}
            value={text}
            placeholder={placeholder}
            disabled={isSubmitting}
            onChange={handleChange}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

const Message = styled.p`
  margin: 0 0 1rem;
`
